import { useEffect, useRef, useCallback } from "react";
import { track } from "@/lib/analytics";

/**
 * D6 — Form abandonment tracking.
 *
 * Wire `bind` into every form input. On the first interaction we fire
 * `form_start`. On unmount or `beforeunload`, if the form was started but
 * never submitted, we fire `form_abandon` with the last touched field +
 * fields-filled count.
 *
 * Usage:
 *   const formTrack = useFormTracking("contact");
 *   <input {...formTrack.bind("name", form.name)} ... />
 *   // on successful submit:
 *   formTrack.markSubmitted();
 */
export function useFormTracking(formName) {
  const started = useRef(false);
  const submitted = useRef(false);
  const lastField = useRef("");
  const filled = useRef(new Set());

  const reportAbandon = useCallback(() => {
    if (!started.current || submitted.current) return;
    submitted.current = true; // guard against double-firing
    track("form_abandon", {
      form_name: formName,
      last_field_touched: lastField.current,
      fields_filled_count: filled.current.size,
    });
  }, [formName]);

  // Fire on tab close / route change away.
  useEffect(() => {
    const onBeforeUnload = () => reportAbandon();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      reportAbandon();
    };
  }, [reportAbandon]);

  const onFocus = useCallback(
    (field) => {
      lastField.current = field;
      if (!started.current) {
        started.current = true;
        track("form_start", { form_name: formName });
      }
    },
    [formName]
  );

  const onChange = useCallback((field, value) => {
    lastField.current = field;
    if (value && String(value).trim().length > 0) {
      filled.current.add(field);
    } else {
      filled.current.delete(field);
    }
  }, []);

  const markSubmitted = useCallback(() => {
    submitted.current = true;
    track("form_submit", {
      form_name: formName,
      fields_filled_count: filled.current.size,
    });
  }, [formName]);

  const bind = useCallback(
    (field, currentValue) => ({
      onFocus: () => onFocus(field),
      onBlur: () => onChange(field, currentValue),
      // Caller still owns onChange — they need to update state. Hook into the
      // currentValue snapshot at blur time which is good enough for filled-count.
    }),
    [onFocus, onChange]
  );

  return { bind, markSubmitted, onFocus, onChange };
}
